import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, register } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c422f3aa-3782-49d4-8e3c-f15c34e2c74c/artifacts/halfbrs0_wizbang.png";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = isLogin 
        ? await login({ email: formData.email, password: formData.password })
        : await register(formData);
      
      localStorage.setItem('wizbang_token', res.data.token);
      localStorage.setItem('wizbang_user', JSON.stringify(res.data.user));
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <img src={LOGO_URL} alt="Wizbang" className="h-12 mx-auto mb-8" />
          <h1 className="font-syne text-3xl font-bold text-white mb-2">
            {isLogin ? 'Admin Login' : 'Create Account'}
          </h1>
          <p className="text-[#888]">
            {isLogin ? 'Sign in to manage your content' : 'Register to get started'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-auth-form">
          {!isLogin && (
            <div>
              <label className="block text-[#888] text-sm mb-2">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required={!isLogin}
                data-testid="admin-name-input"
                className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                placeholder="Your name"
              />
            </div>
          )}

          <div>
            <label className="block text-[#888] text-sm mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="admin-email-input"
              className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
              placeholder="admin@wizbang.in"
            />
          </div>

          <div>
            <label className="block text-[#888] text-sm mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="admin-password-input"
                className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors pr-12"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#555] hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-submit-btn"
            className="w-full bg-[#ff5a1f] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors duration-300 btn-press flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isLogin ? 'Signing in...' : 'Creating account...'}
              </>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#888] text-sm">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#ff5a1f] ml-2 hover:underline"
            >
              {isLogin ? 'Register' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
